import FuseUtils from '@fuse/utils/FuseUtils';
import axios from 'axios';
import routes from 'hooks/api';
import { api, processNextRequest, requestQueue } from 'hooks/api/main-request/base';
import { setXSRFToken } from 'utils/setXSRFToken';
import { isReady } from './isReady';
/* eslint-disable camelcase */

class JwtService extends FuseUtils.EventEmitter {
	init() {
		console.log('init');
		this.getLoginnedUsers();
		this.handleAuthentication();
		this.getPrinterConfig();

		window.addEventListener('beforeunload', () => {
			const remainingTabs = Object.keys(sessionStorage).length;
			if (remainingTabs === 1) {
				const lastAccount = JSON.parse(sessionStorage.getItem('activeAccount'));
				if (lastAccount) {
					this.setLastActiveAccount(lastAccount?.phoneNumber);
				}
			}
		});
	}

	handleAuthentication = () => {
		const activeAccount = sessionStorage.getItem('activeAccount');

		if (!activeAccount) {
			const lastActiveAccount = localStorage.getItem('lastActiveAccount');
			if (lastActiveAccount) {
				sessionStorage.setItem('activeAccount', lastActiveAccount);
			}
		}

		setTimeout(() => {
			isReady.resolve();
		}, 1200);

		const userId = this.getUserId();

		if (!userId) {
			this.emit('onNoAccessToken');

			return;
		}

		this.emit('onAutoLogin', true);
	};

	signInWithPhoneNumberAndPassword = (phoneNumber, password, oneTimePassword) => {
		return new Promise((resolve, reject) => {
			axios
				.post(
					routes.signIn,
					{
						phoneNumber: String(phoneNumber)?.trim(),
						password,
						oneTimePassword
					},
					{
						withCredentials: true
					}
				)
				.then(response => {
					const { id, role, phoneNumber, fullName } = response?.data;

					if (response.data) {
						const xsrfToken = response.headers['xsrf-token'];
						const newAccount = {
							id,
							role,
							fullName,
							phone: phoneNumber
						};
						this.addAccount(newAccount);
						sessionStorage.setItem('activeAccount', JSON.stringify(newAccount));
						this.setLastActiveAccount(phoneNumber);

						setXSRFToken(xsrfToken);

						window.location.reload();
					} else {
						reject(response.data.error);
					}
				})
				.catch(err => {
					reject(err);
				});
		});
	};

	signInWithToken = () => {
		return new Promise((resolve, reject) => {
			const UserId = this.getUserId();

			api.defaults.headers.common.UserId = UserId;
			api.defaults.headers.common['XSRF-TOKEN'] = this.getXSRFToken();

			api.get(routes.accessToken)
				.then(response => {
					if (response.data) {
						console.log('data', response.data);
						if (response.data.isBlocked) return window.location.reload();

						resolve(response.data);
					} else {
						this.logout();
						reject(new Error('Failed to login with token. on response'));
					}
				})
				.catch(async err => {
					reject(new Error('Failed to login with token. on error'));
				});
		});
	};

	getLoginnedUsers = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(routes.getLoginnedUsers, { withCredentials: true })
				.then(response => {
					const users = response?.data || [];

					if (users.length === 0) {
						this.clearAccounts();
						return resolve([]);
					}

					const accountsMap = users.reduce((acc, account) => {
						acc[account.phoneNumber] = {
							id: account.id,
							role: account.role,
							fullName: account.fullName,
							phone: account.phoneNumber
						};
						return acc;
					}, {});

					localStorage.setItem('accounts', JSON.stringify(accountsMap));

					const activeAccount = JSON.parse(sessionStorage.getItem('activeAccount'));

					if (!users.some(account => account.id === activeAccount?.id)) {
						const defaultAccount = users[0]?.phoneNumber;

						this.setLastActiveAccount(defaultAccount);
						sessionStorage.setItem('activeAccount', JSON.stringify(users[0] ?? {}));
					}

					resolve(response);
				})
				.catch(err => {
					reject(err);
				});
		});
	};

	getPrinterConfig = () => {
		return new Promise((resolve, reject) => {
			const printerHost = localStorage?.getItem('printerHost') || '127.0.0.1';

			axios
				.get(`http://${printerHost}:4567/get`)
				.then(response => {
					if (response.data?.config?.thermal_ip) {
						localStorage.setItem('printerIP', response.data.config.thermal_ip);
					}

					if (response.data?.config?.simple_printer) {
						localStorage.setItem('selectedPrinter', response.data.config.simple_printer);
					}

					resolve(response);
				})
				.catch(err => {
					reject(err);
				});
		});
	};

	checkActiveAccount = phone => {
		try {
			const accounts = JSON.parse(localStorage.getItem('accounts') || '{}');
			const activeAccount = JSON.parse(sessionStorage.getItem('activeAccount'));

			if (!accounts[phone]) {
				console.log('Account not found!');
				return;
			}

			if (accounts[phone]?.id === activeAccount?.id) {
				delete accounts[phone];

				const remainingAccounts = Object.values(accounts);
				const nextActiveAccount = remainingAccounts[0] || null;

				localStorage.setItem('accounts', JSON.stringify(accounts));
				sessionStorage.setItem('activeAccount', JSON.stringify(nextActiveAccount || {}));
				this.setLastActiveAccount(nextActiveAccount?.phone || null);

				console.log('Account removed and switched!');
			} else {
				console.log('Account does not match the active account!');
			}
		} catch (error) {
			console.error('Error in checkActiveAccount:', error);
		}
	};

	getAdminOTP = async phoneNumber => {
		try {
			const response = await axios.get(`${routes.getOTP}?phone=${String(phoneNumber).trim()}`, {
				withCredentials: true
			});
			return response;
		} catch (error) {
			console.error('Error fetching OTP:', error);
			throw error;
		}
	};

	addAccount = newAccount => {
		const accounts = JSON.parse(localStorage.getItem('accounts') || '{}');

		accounts[newAccount?.phone] = newAccount;
		localStorage.setItem('accounts', JSON.stringify(accounts));

		console.log(`Account for phone ${newAccount?.phone} has been added or updated.`);
	};

	switchAccount = (phone, release) => {
		const accounts = JSON.parse(localStorage.getItem('accounts') || '{}');
		const selectedAccount = accounts[phone];

		if (requestQueue.length) {
			requestQueue.push(this.switchAccount.bind(this, phone, true));
			return;
		}

		isReady.restart();

		this.setLastActiveAccount(phone);

		if (selectedAccount) {
			sessionStorage.setItem('activeAccount', JSON.stringify(selectedAccount));
			window.location.pathname = '/';
			console.log(`Switched to account: ${selectedAccount?.name}`);
		} else {
			console.log('Account not found!');
		}

		if (release) {
			processNextRequest();
		}
	};

	removeAccount = phone => {
		const accounts = JSON.parse(localStorage.getItem('accounts') || '{}');

		if (accounts[phone]) {
			delete accounts[phone];
			localStorage.setItem('accounts', JSON.stringify(accounts));

			console.log('Account removed successfully!');
		} else {
			console.log('Account not found!');
		}
	};

	setLastActiveAccount = phone => {
		const accounts = JSON.parse(localStorage.getItem('accounts') || '{}');
		const selectedAccount = accounts[phone];

		if (selectedAccount) localStorage.setItem('lastActiveAccount', JSON.stringify(selectedAccount));
	};

	clearAccounts = () => {
		try {
			localStorage.removeItem('accounts');
			localStorage.removeItem('xsrf-tokens');
			localStorage.removeItem('lastActiveAccount');

			sessionStorage.clear();
		} catch (error) {
			console.error('Error during clearing store:', error);
			throw error;
		}
	};

	logout = () => {
		this.clearAccounts();
	};

	logoutWithApi = UserId => {
		return new Promise((resolve, reject) => {
			const client = UserId ? axios : api;
			const config = UserId ? { withCredentials: true, headers: { UserId } } : undefined;

			return client
				.get(routes.logout, config)
				.then(response => {
					const active = JSON.parse(sessionStorage.getItem('activeAccount') ?? 'null');

					this.removeAccount(active?.phone);
					this.getLoginnedUsers();

					resolve(response);
				})
				.catch(err => {
					reject(err);
				})
				.finally(() => {
					window.location.reload();
				});
		});
	};

	getXSRFToken = () => {
		try {
			const tokens = JSON.parse(localStorage.getItem('xsrf-tokens') || '{}');
			const activeAccount = JSON.parse(sessionStorage.getItem('activeAccount') || 'null');

			return activeAccount?.id ? tokens[activeAccount.id] : undefined;
		} catch (error) {
			console.error('Error retrieving XSRF token:', error);
			return undefined;
		}
	};

	getUserId = () => {
		try {
			return JSON.parse(sessionStorage.getItem('activeAccount') || 'null')?.id;
		} catch (error) {
			console.error('Error retrieving user ID:', error);
			return undefined;
		}
	};
}

const instance = new JwtService();

export default instance;
